import { classToPlain, Exclude, Expose, plainToClass } from "class-transformer";
import * as t from "io-ts";
import "reflect-metadata";
import { validateJSON } from "../web/validation";
import { CargoSubtype, toCargoSubtype } from "./cargo-subtype";

/** Validator for a container alert cargo subtype options. */
export const ContainerAlertCargoSubtypeOptionsValidator = t.intersection([
  t.type({
    id: t.string,
    containerAlertOptionsId: t.string,
    highLevelPercent: t.number,
    highCriticalPercent: t.number,
    intermIncreasePercent: t.number,
    intermDecreasePercent: t.number,
    lowLevelPercent: t.number,
    lowCriticalPercent: t.number,
    unchangedMaxInterval: t.number,
    unchangedMaxIntervalUom: t.string,
    minimumIncreaseChangePercent: t.number,
    minimumIncreaseChangeInterval: t.number,
    minimumIncreaseChangeIntervalUom: t.string,
    minimumDecreaseChangePercent: t.number,
    minimumDecreaseChangeInterval: t.number,
    minimumDecreaseChangeIntervalUom: t.string,
    autoDispatchRequest: t.boolean,
    autoDispatch: t.boolean,
    driverLevelSubmit: t.boolean,
    fieldLevelSubmit: t.boolean,
    meterLevelSubmit: t.boolean,
    cargoSubtype: t.string,
  }),
  t.partial({
    dispatchRequestDeliverySiteId: t.string,
    dispatchRequestDeliveryContainerId: t.string,
    dispatchRequestServiceTypeId: t.string,
    regionId: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const ContainerAlertCargoSubtypeOptionsArrayValidator = t.array(
  ContainerAlertCargoSubtypeOptionsValidator
);

export type ContainerAlertCargoSubtypeOptions = t.TypeOf<
  typeof ContainerAlertCargoSubtypeOptionsValidator
>;
export type ContainerAlertCargoSubtypeOptionsArray = t.TypeOf<
  typeof ContainerAlertCargoSubtypeOptionsArrayValidator
>;

/**
 * A container alert options.
 */
@Exclude()
export class ContainerAlertCargoSubtypeOptionsEntity {
  @Expose() id!: string;
  @Expose() regionId?: string;
  @Expose() containerAlertOptionsId!: string;
  @Expose() highLevelPercent = 0;
  @Expose() highCriticalPercent = 0;
  @Expose() intermIncreasePercent = 0;
  @Expose() intermDecreasePercent = 0;
  @Expose() lowLevelPercent = 0;
  @Expose() lowCriticalPercent = 0;
  @Expose() unchangedMaxInterval = 0;
  @Expose() unchangedMaxIntervalUom = 'hours';
  @Expose() minimumIncreaseChangePercent = 0;
  @Expose() minimumIncreaseChangeInterval = 0;
  @Expose() minimumIncreaseChangeIntervalUom = 'hours';
  @Expose() minimumDecreaseChangePercent = 0;
  @Expose() minimumDecreaseChangeInterval = 0;
  @Expose() minimumDecreaseChangeIntervalUom = 'hours';
  @Expose() autoDispatchRequest = false;
  @Expose() autoDispatch = false;
  @Expose() dispatchRequestDeliverySiteId?: string;
  @Expose() dispatchRequestDeliveryContainerId?: string;
  @Expose() dispatchRequestServiceTypeId?: string;
  @Expose() driverLevelSubmit = false;
  @Expose() fieldLevelSubmit = false;
  @Expose() meterLevelSubmit = true;
  @Expose({ name: 'cargoSubtype' }) private _cargoSubtype!: string;
  @Expose() private createDttm?: number;
  @Expose() private lastModifiedDttm?: number;

  /**
   * Constructor for ContainerAlertCargoSubtypeOptionsEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  // TODO: add create function

  /**
   * Create a ContainerAlertCargoSubtypeOptionsEntity from a ContainerAlertCargoSubtypeOptions type value.
   *
   * @param containerAlertCargoSubtypeOptions The container alert options type value.
   */
  static from(
    containerAlertCargoSubtypeOptions: ContainerAlertCargoSubtypeOptions
  ): ContainerAlertCargoSubtypeOptionsEntity {
    const entity: ContainerAlertCargoSubtypeOptionsEntity = plainToClass(
      ContainerAlertCargoSubtypeOptionsEntity,
      containerAlertCargoSubtypeOptions,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  /**
   * Deserializes the ContainerAlertCargoSubtypeOptionsEntity to a ContainerAlertCargoSubtypeOptions type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<ContainerAlertCargoSubtypeOptions> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ContainerAlertCargoSubtypeOptionsValidator, object);
  }

  get cargoSubtype(): CargoSubtype {
    return toCargoSubtype(this._cargoSubtype);
  }

  set cargoSubtype(type: CargoSubtype) {
    this._cargoSubtype = type.code;
  }

  get createDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.createDttm) {
      return new Date(this.createDttm);
    } else {
      return undefined;
    }
  }

  set createDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.createDttm = date.getTime();
    } else {
      this.createDttm = undefined;
    }
  }

  get lastModifiedDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.lastModifiedDttm) {
      return new Date(this.lastModifiedDttm);
    } else {
      return undefined;
    }
  }

  set lastModifiedDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.lastModifiedDttm = date.getTime();
    } else {
      this.lastModifiedDttm = undefined;
    }
  }
}
